import React, { useState, useMemo } from 'react';
import { Mafs, Coordinates, Point, Line, Text, Theme } from 'mafs';
import { Slider } from 'antd';

// Helper Functions
const generateSequence = (type, a1, param, n) => {
    const terms = [];
    for (let i = 0; i < n; i++) {
        if (type === 'arithmetic') {
            terms.push(a1 + i * param);
        } else if (type === 'geometric') {
            terms.push(a1 * Math.pow(param, i));
        }
    }
    return terms;
};

const partialSum = (type, a1, param, n) => {
    if (type === 'arithmetic') {
        return (n / 2) * (2 * a1 + (n - 1) * param);
    } else {
        if (param === 1) return n * a1;
        return (a1 * (1 - Math.pow(param, n))) / (1 - param);
    }
};

const formatNum = (val) => {
    if (val === null || val === undefined || isNaN(val)) return '0';
    return Number.isInteger(val) ? String(val) : val.toFixed(2);
};

export const SequenceVisualizer = ({ config }) => {
    const [sequenceType, setSequenceType] = useState(config.sequenceType || 'arithmetic');
    const [firstTerm, setFirstTerm] = useState(config.firstTerm || 2);
    const [commonDiff, setCommonDiff] = useState(config.commonDifference || 3);
    const [commonRatio, setCommonRatio] = useState(config.commonRatio || 2);
    const [termCount, setTermCount] = useState(config.termCount || 8);
    const [highlightTerm, setHighlightTerm] = useState(config.highlightTerm || null);

    const param = sequenceType === 'arithmetic' ? commonDiff : commonRatio;

    const sequence = useMemo(() =>
            generateSequence(sequenceType, firstTerm, param, termCount),
        [sequenceType, firstTerm, param, termCount]
    );

    const studentSequence = config.studentSequence || null;

    const renderGraph = () => {
        const points = sequence.map((value, index) => [index + 1, value]);
        const maxY = Math.max(...sequence) * 1.2;
        const minY = Math.min(0, ...sequence);

        return (
            <div style={{ marginTop: '20px' }}>
                <Mafs viewBox={{ x: [0, termCount + 1], y: [minY, maxY] }} height={300} pan={false}>
                    <Coordinates.Cartesian xAxis={{ lines: 1, labels: (n) => String(n) }} yAxis={{ lines: true }} />

                    {points.map(([x, y], i) => (
                        <Point
                            key={`pt-${i}`}
                            x={x}
                            y={y}
                            color={highlightTerm === i + 1 ? Theme.orange : Theme.blue}
                        />
                    ))}

                    {points.slice(0, -1).map(([x1, y1], i) => {
                        const [x2, y2] = points[i + 1];
                        return (
                            <Line.Segment
                                key={`line-${i}`}
                                point1={[x1, y1]}
                                point2={[x2, y2]}
                                color={Theme.blue}
                                style="dashed"
                            />
                        );
                    })}
                </Mafs>
            </div>
        );
    };

    const renderCalculations = () => {
        if (!highlightTerm) return null;
        const n = highlightTerm;
        const val = sequence[n - 1];
        const sum = partialSum(sequenceType, firstTerm, param, n);

        return (
            <div className="ch-calculations" style={{ marginTop: '20px', padding: '20px', background: '#fff7e6', border: '2px solid #ffc53d', borderRadius: '8px' }}>
                <h4 style={{ marginTop: 0, color: '#d48806' }}>📊 Calculations for n = {n}</h4>
                {sequenceType === 'arithmetic' ? (
                    <div style={{ lineHeight: '1.8' }}>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            aₙ = a₁ + (n-1)d
                        </p>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            a₍{n}₎ = {formatNum(firstTerm)} + ({n}-1) × {formatNum(commonDiff)}
                        </p>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            a₍{n}₎ = {formatNum(firstTerm)} + {formatNum((n-1) * commonDiff)}
                        </p>
                        <p style={{ margin: '10px 0 5px 0', fontFamily: 'monospace', fontSize: '16px', fontWeight: 'bold', color: '#d48806' }}>
                            a₍{n}₎ = {formatNum(val)}
                        </p>
                    </div>
                ) : (
                    <div style={{ lineHeight: '1.8' }}>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            aₙ = a₁ × rⁿ⁻¹
                        </p>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            a₍{n}₎ = {formatNum(firstTerm)} × {formatNum(commonRatio)}^{n-1}
                        </p>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                            a₍{n}₎ = {formatNum(firstTerm)} × {formatNum(Math.pow(commonRatio, n-1))}
                        </p>
                        <p style={{ margin: '10px 0 5px 0', fontFamily: 'monospace', fontSize: '16px', fontWeight: 'bold', color: '#d48806' }}>
                            a₍{n}₎ = {formatNum(val)}
                        </p>
                    </div>
                )}
                {config.showPartialSum !== false && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '2px solid #ffe58f' }}>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px', fontWeight: 500 }}>
                            Partial Sum: S₍{n}₎ = <strong style={{ color: '#d48806' }}>{formatNum(sum)}</strong>
                        </p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="ch-sequence-visualizer">
            <div style={{ padding: '15px', background: '#f0f5ff', borderRadius: '8px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 600 }}>
                    {sequenceType === 'arithmetic' ? 'Arithmetic Sequence' : 'Geometric Sequence'}
                </h3>
                <div style={{ fontFamily: 'monospace', fontSize: '15px', color: '#1890ff' }}>
                    {sequenceType === 'arithmetic' ? (
                        <>aₙ = a₁ + (n-1)d where a₁ = {formatNum(firstTerm)}, d = {formatNum(commonDiff)}</>
                    ) : (
                        <>aₙ = a₁ × rⁿ⁻¹ where a₁ = {formatNum(firstTerm)}, r = {formatNum(commonRatio)}</>
                    )}
                </div>
            </div>

            <div className="ch-controls" style={{ padding: '15px', background: '#fafafa', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ marginBottom: '15px' }}>
                    <button
                        onClick={() => setSequenceType(sequenceType === 'arithmetic' ? 'geometric' : 'arithmetic')}
                        style={{ padding: '8px 16px', background: '#1890ff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}
                    >
                        Switch to {sequenceType === 'arithmetic' ? 'Geometric' : 'Arithmetic'}
                    </button>
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                        First Term (a₁): {formatNum(firstTerm)}
                    </label>
                    <Slider
                        min={-10}
                        max={10}
                        step={1}
                        value={firstTerm}
                        onChange={setFirstTerm}
                    />
                </div>

                {sequenceType === 'arithmetic' ? (
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                            Common Difference (d): {formatNum(commonDiff)}
                        </label>
                        <Slider
                            min={-5}
                            max={5}
                            step={0.5}
                            value={commonDiff}
                            onChange={setCommonDiff}
                        />
                    </div>
                ) : (
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                            Common Ratio (r): {formatNum(commonRatio)}
                        </label>
                        <Slider
                            min={0.5}
                            max={3}
                            step={0.25}
                            value={commonRatio}
                            onChange={setCommonRatio}
                        />
                    </div>
                )}

                <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                        Number of Terms: {termCount}
                    </label>
                    <Slider
                        min={3}
                        max={15}
                        step={1}
                        value={termCount}
                        onChange={setTermCount}
                    />
                </div>
            </div>

            {renderGraph()}

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
                {sequence.map((val, i) => (
                    <button
                        key={i}
                        onClick={() => setHighlightTerm(i + 1)}
                        style={{ padding: '8px 12px', background: highlightTerm === i + 1 ? '#fa8c16' : '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        n={i + 1}: {formatNum(val)}
                    </button>
                ))}
            </div>

            {renderCalculations()}
        </div>
    );
};